import { BaseComponent, BuildingModel, ComponentDocument, RoomModel } from '../types/protocols';
import { RoomImpl } from '../types/implementations';
import { FileAttachment } from '../types/attachments';

/**
 * Document sources that live inside the building model itself, shared by the
 * building documents page (its sections) and the building page (its counter)
 * so the two cannot drift.
 */

export interface ComponentDocumentEntry {
    componentKey: string;
    componentType: string;
    componentLabel?: string;
    document: ComponentDocument;
}

export interface RiskAttachmentEntry {
    roomKey: string;
    room: RoomImpl;
    attachment: FileAttachment;
}

/**
 * Documents of every non-archived component (utilities included), sorted by
 * component type then document name. Archived (replaced) components are
 * skipped: their documents are still reachable from the component itself.
 */
export const extractComponentDocuments = (
    components: { [key: string]: BaseComponent } | undefined
): ComponentDocumentEntry[] => {
    if (!components) return [];

    const results: ComponentDocumentEntry[] = [];
    Object.entries(components).forEach(([componentKey, componentData]) => {
        if (componentData.archivedAt) return;
        const documents = componentData.documents;
        if (!documents) return;
        Object.values(documents).forEach((doc: ComponentDocument) => {
            results.push({
                componentKey,
                componentType: componentData.specificType?.value || '',
                componentLabel: componentData.label?.value || undefined,
                document: doc,
            });
        });
    });

    results.sort((a, b) => {
        const typeCompare = a.componentType.localeCompare(b.componentType);
        if (typeCompare !== 0) return typeCompare;
        return a.document.name.localeCompare(b.document.name);
    });

    return results;
};

/** Work-environment-risk attachments of every room, sorted by room name. */
export const extractRiskAttachments = (
    rooms: { [key: string]: RoomModel } | undefined
): RiskAttachmentEntry[] => {
    if (!rooms) return [];

    const results: RiskAttachmentEntry[] = [];
    Object.entries(rooms).forEach(([roomKey, roomData]) => {
        const room = new RoomImpl(roomData);
        const attachments = room.workEnvironmentRisk?.attachments;
        if (!attachments) return;
        Object.values(attachments).forEach(attachment => {
            results.push({ roomKey, room, attachment });
        });
    });

    results.sort((a, b) => a.room.displayName().localeCompare(b.room.displayName()));

    return results;
};

/**
 * Number of documents the building model holds: building documents,
 * non-archived component documents and room risk attachments. Files that
 * need a separate fetch (projects, leases, work orders, maintenance) are not
 * counted.
 */
export const countBuildingModelDocuments = (model: BuildingModel | undefined): number => {
    if (!model) return 0;
    const buildingDocs = model.documents ? Object.keys(model.documents).length : 0;
    return buildingDocs
        + extractComponentDocuments(model.structure?.components).length
        + extractRiskAttachments(model.structure?.rooms).length;
};
